import { ABP, ListService, PagedResultDto, TrackByService } from '@abp/ng.core';
import { Confirmation, ConfirmationService } from '@abp/ng.theme.shared';
import { DateAdapter } from '@abp/ng.theme.shared/extensions';
import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbDateAdapter } from '@ng-bootstrap/ng-bootstrap';
import { filter, finalize, switchMap, tap } from 'rxjs/operators';
import type {
  GetStatsInput,
  StatDto,
} from '../../proxy/stats/models';
import { StatService } from '../../proxy/stats/stat.service';

@Component({
  selector: 'app-stat',
  changeDetection: ChangeDetectionStrategy.Default,
  providers: [ListService, { provide: NgbDateAdapter, useClass: DateAdapter }],
  templateUrl: './stat.component.html',
  styles: [],
})
export class StatComponent implements OnInit {
  data: PagedResultDto<StatDto> = {
    items: [],
    totalCount: 0,
  };

  filters = {} as GetStatsInput;

  form: FormGroup;

  isFiltersHidden = true;

  isModalBusy = false;

  isModalOpen = false;

  selected?: StatDto;

  constructor(
    public readonly list: ListService,
    public readonly track: TrackByService,
    public readonly service: StatService,
    private confirmation: ConfirmationService,
    private fb: FormBuilder
  ) {}

  ngOnInit() {
    const getData = (query: ABP.PageQueryParams) =>
      this.service.getList({
        ...query,
        ...this.filters,
        filterText: query.filter,
      });

    const setData = (list: PagedResultDto<StatDto>) => (this.data = list);

    this.list.hookToQuery(getData).subscribe(setData);
  }

  clearFilters() {
    this.filters = {} as GetStatsInput;
  }

  buildForm() {
    const {
      viewsCount,
      statDate,
      userCount,
      assetCount,
      linkCount,
      scenesCount,
      storiesCount,
    } = this.selected || {};

    this.form = this.fb.group({
      viewsCount: [viewsCount ?? null, []],
      statDate: [statDate ? new Date(statDate) : null, []],
      userCount: [userCount ?? null, []],
      assetCount: [assetCount ?? null, []],
      linkCount: [linkCount ?? null, []],
      scenesCount: [scenesCount ?? null, []],
      storiesCount: [storiesCount ?? null, []],
    });
  }

  hideForm() {
    this.isModalOpen = false;
    this.form.reset();
  }

  showForm() {
    this.buildForm();
    this.isModalOpen = true;
  }

  submitForm() {
    if (this.form.invalid) return;

    const request = this.selected
      ? this.service.update(this.selected.id, this.form.value)
      : this.service.create(this.form.value);

    this.isModalBusy = true;

    request
      .pipe(
        finalize(() => (this.isModalBusy = false)),
        tap(() => this.hideForm()),
      )
      .subscribe(this.list.get);
  }

  create() {
    this.selected = undefined;
    this.showForm();
  }

  update(record: StatDto) {
    this.selected = record;
    this.showForm();
  }

  delete(record: StatDto) {
    this.confirmation.warn(
      'Analysis::DeleteConfirmationMessage',
      'Analysis::AreYouSure',
      { messageLocalizationParams: [] }
    ).pipe(
      filter(status => status === Confirmation.Status.confirm),
      switchMap(() => this.service.delete(record.id)),
    ).subscribe(this.list.get);
  }
}
